/**
 * Crowlingo Design System - Centralized Color Definitions
 *
 * This is the SINGLE SOURCE OF TRUTH for all colors in the application.
 * All other files (Tailwind, SCSS, hooks) should import from here.
 *
 * Architecture:
 * - Semantic colors (success, error, warning, info) are static
 * - Grey scale is static
 * - Accent/theme colors are dynamic via CSS variables
 * - Surface/content colors adapt to day/night mode via CSS variables
 */

// ============================================================================
// Default Accent Color
// ============================================================================

/**
 * Default accent color (indigo) used when no workspace theme is set
 * Appliqué sur: boutons primaires, liens, éléments interactifs focus,
 * indicateurs de sélection, badges actifs, bordures d'accent
 */
export const DEFAULT_ACCENT = "#6366F1";

// ============================================================================
// Semantic Colors (Static)
// ============================================================================

export const semanticColors = {
  // Success: Messages de succès, badges de statut positif, validations, 
  // indicateurs de progression complète, toasts de confirmation
  success: {
    light: "#b9f6ca", // Arrière-plans de badges, highlights légers
    main: "#00e676", // Icônes, textes de succès, bordures actives
    dark: "#00c853", // États hover/pressed, accents forts
  },
  // Error: Messages d'erreur, champs invalides, alertes critiques,
  // badges de statut négatif, indicateurs de suppression
  error: {
    light: "#ef9a9a", // Arrière-plans d'alertes, highlights d'erreur
    main: "#f44336", // Icônes d'erreur, bordures invalides, textes d'erreur
    dark: "#c62828", // États hover/pressed, accents critiques
  },
  // Warning: Avertissements, alertes non-critiques, états en attente,
  // indicateurs de prudence, notifications d'attention requise
  warning: {
    light: "#fff8e1", // Arrière-plans de banners d'avertissement
    main: "#ffe57f", // Icônes d'avertissement, bordures d'alerte
    dark: "#ffc107", // États hover/pressed, accents d'avertissement
  },
  // Info: Messages informatifs, tooltips, hints, aides contextuelles,
  // liens secondaires, indicateurs neutres
  info: {
    light: "#e3f2fd", // Arrière-plans de callouts informatifs
    main: "#2196f3", // Icônes d'info, liens, textes informatifs
    dark: "#1976d2", // États hover/pressed, accents informatifs
  },
  // Orange: Indicateurs spéciaux, highlights temporaires, notifications,
  // états intermédiaires, marqueurs d'attention secondaires
  orange: {
    light: "#fbe9e7", // Arrière-plans de highlights
    main: "#ffab91", // Icônes, badges, indicateurs
    dark: "#d84315", // États hover/pressed, accents
  },
  // Neutral: États désactivés, placeholders, séparateurs,
  // éléments inactifs, textes tertiaires
  neutral: {
    light: "#f5f5f5", // Arrière-plans désactivés, zones inactives
    main: "#9e9e9e", // Icônes désactivées, bordures subtiles
    dark: "#616161", // Textes désactivés, séparateurs forts
  },
} as const;

// ============================================================================
// Grey Scale (Static)
// ============================================================================

export const greyScale = {
  50: "#fafafa", // Arrière-plans les plus clairs, zones hover subtiles
  100: "#f5f5f5", // Arrière-plans secondaires, cards en mode jour
  200: "#eeeeee", // Séparateurs légers, bordures subtiles
  300: "#e0e0e0", // Bordures standard, dividers
  400: "#c1c1c1", // Icônes désactivées, placeholders
  500: "#9e9e9e", // Textes tertiaires, labels secondaires
  600: "#757575", // Textes secondaires, icônes inactives
  700: "#616161", // Textes de sous-titres, labels
  800: "#424242", // Textes principaux (mode sombre), headers
  900: "#212121", // Textes les plus foncés, titres importants
} as const;

// ============================================================================
// Chart Colors (Static fallback palette)
// ============================================================================

/**
 * Default chart colors used as fallback when theme colors aren't available
 * These are vibrant, distinct colors optimized for data visualization
 * 
 * Utilisés sur: graphiques (lignes, barres, aires, camemberts), légendes,
 * séries de données, indicateurs de catégories dans les visualisations
 */
export const chartColors = {
  blue: "#3B82F6", // Série principale, données primaires
  green: "#22C55E", // Données positives, croissance, succès
  orange: "#F97316", // Alertes, données importantes, highlights
  violet: "#8B5CF6", // Catégories secondaires, données complémentaires
  pink: "#EC4899", // Données tertiaires, variations
  teal: "#14B8A6", // Données alternatives, comparaisons
  yellow: "#EAB308", // Attention, données en attente, warnings
  red: "#EF4444", // Données négatives, déclin, erreurs
} as const;

/**
 * Chart colors as an array for easy iteration
 */
export const chartColorsArray = [
  chartColors.blue,
  chartColors.green,
  chartColors.orange,
  chartColors.violet,
  chartColors.pink,
  chartColors.teal,
  chartColors.yellow,
  chartColors.red,
] as const;

// ============================================================================
// Day/Night Mode Surface Colors
// ============================================================================

/**
 * Couleurs de surface pour le mode jour (light mode)
 * Appliquées sur: conteneurs, cards, modales, pages, zones de contenu
 */
export const daySurfaces = {
  surface: "#FAFBFC", // Arrière-plan principal de page, conteneur racine
  surfaceSecondary: "#F4F5F7", // Cards, panneaux latéraux, sections distinctes
  surfaceTertiary: "#EBEDF0", // Zones imbriquées, sous-sections, inputs désactivés
  surfaceElevated: "#FFFFFF", // Modales, dropdowns, popovers, éléments flottants
  contentPrimary: "#1F2937", // Titres, textes principaux, labels importants
  contentSecondary: "#6B7280", // Descriptions, textes secondaires, sous-titres
  contentTertiary: "#9CA3AF", // Placeholders, hints, textes désactivés
  contentMuted: "#6B7280", // Textes atténués, indicateurs inactifs (même que secondary pour bon contraste)
  contentInverse: "#FFFFFF", // Textes sur fonds colorés/sombres, boutons primaires
  border: "#E5E7EB", // Bordures de cards, séparateurs standards
  borderStrong: "#D1D5DB", // Bordures d'inputs focus, séparateurs visibles
  borderSubtle: "#F3F4F6", // Bordures très légères, dividers subtils
} as const;

/**
 * Couleurs de surface pour le mode nuit (dark mode)
 * Appliquées sur: conteneurs, cards, modales, pages, zones de contenu
 */
export const nightSurfaces = {
  surface: "#1e1e28", // Arrière-plan principal de page, conteneur racine
  surfaceSecondary: "#252532", // Cards, panneaux latéraux, sections distinctes
  surfaceTertiary: "#2d2d3a", // Zones imbriquées, sous-sections, inputs désactivés
  surfaceElevated: "#383848", // Modales, dropdowns, popovers, éléments flottants
  contentPrimary: "#f0f0f5", // Titres, textes principaux, labels importants
  contentSecondary: "#a0a0b0", // Descriptions, textes secondaires, sous-titres
  contentTertiary: "#8a8a9a", // Placeholders, hints, textes désactivés
  contentMuted: "#a0a0b0", // Textes atténués, indicateurs inactifs (même que secondary pour bon contraste)
  contentInverse: "#1e1e28", // Textes sur fonds clairs, boutons inversés
  border: "#4a4a5a", // Bordures de cards, séparateurs standards
  borderStrong: "#5a5a6a", // Bordures d'inputs focus, séparateurs visibles
  borderSubtle: "#3a3a48", // Bordures très légères, dividers subtils
} as const;

// ============================================================================
// Sidebar Colors (Static - always uses dark mode style)
// ============================================================================

/**
 * Couleurs spécifiques à la sidebar de navigation (ArcSidebar)
 * Appliquées sur: menu latéral principal, navigation, liste des trackers/workspaces
 */
export const sidebarColors = {
  day: {
    background: "#f5f5f7", // Fond principal de la sidebar
    textPrimary: "#1a1a1a", // Titres de sections, noms de trackers actifs
    textSecondary: "rgba(0, 0, 0, 0.55)", // Labels secondaires, compteurs, timestamps
    gradientStart: "#f5f5f7", // Début du dégradé de fond
    gradientMiddle: "#f0f0f2", // Milieu du dégradé de fond
    gradientEnd: "#eaeaec", // Fin du dégradé de fond
    borderAccent: "#e0e0e4", // Séparateurs entre sections, bordures d'items
  },
  night: {
    background: "#1e1e28", // Fond principal de la sidebar
    textPrimary: "#FFFFFF", // Titres de sections, noms de trackers actifs
    textSecondary: "rgba(255, 255, 255, 0.65)", // Labels secondaires, compteurs, timestamps
    gradientStart: "#1e1e28", // Début du dégradé de fond
    gradientMiddle: "#1a1a24", // Milieu du dégradé de fond
    gradientEnd: "#161620", // Fin du dégradé de fond
    borderAccent: "#3a3a48", // Séparateurs entre sections, bordures d'items
  },
} as const;

// ============================================================================
// Lucene Syntax Highlighting Colors
// ============================================================================

/**
 * Couleurs de coloration syntaxique pour les requêtes Lucene/filtres
 * Appliquées sur: éditeur de filtres, barre de recherche avancée,
 * prévisualisation de requêtes, tokens de filtre
 */
export const syntaxColors = {
  day: {
    field: "#2563eb", // Noms de champs (ex: "title:", "author:")
    operator: "#9333ea", // Opérateurs logiques (AND, OR, NOT)
    value: "#16a34a", // Valeurs non-quotées, termes simples
    quoted: "#ea580c", // Chaînes entre guillemets ("exact match")
    range: "#4f46e5", // Plages de valeurs ([min TO max], intervalles)
  },
  night: {
    field: "#60a5fa", // Noms de champs (ex: "title:", "author:")
    operator: "#c084fc", // Opérateurs logiques (AND, OR, NOT)
    value: "#4ade80", // Valeurs non-quotées, termes simples
    quoted: "#fb923c", // Chaînes entre guillemets ("exact match")
    range: "#818cf8", // Plages de valeurs ([min TO max], intervalles)
  },
} as const;

// ============================================================================
// Status Colors (for board views, badges, indicators)
// ============================================================================

/**
 * Couleurs de statut pour les vues board, badges, indicateurs
 * Appliquées sur: colonnes de board, badges de statut, indicateurs d'état
 */
export const statusColors = {
  active: "#10B981", // Éléments actifs, colonnes "Actifs"
  inactive: "#6B7280", // Éléments inactifs, colonnes "Inactifs"
  import: "#3B82F6", // Éléments d'import, colonnes "Import"
  draft: "#F59E0B", // Brouillons, éléments en cours
  monitoring: "#10B981", // Trackers de monitoring
} as const;

// ============================================================================
// Enrichment Type Colors
// ============================================================================

/**
 * Couleurs pour les types d'enrichissement
 * Appliquées sur: icônes d'enrichissement, colonnes de board par type
 */
export const enrichmentTypeColors: Record<string, string> = {
  named_entity_recognition: "#3B82F6",
  speech_to_text: "#10B981",
  classification: "#8B5CF6",
  sentiment_analysis: "#EC4899",
  language_detection: "#F59E0B",
  keyword_extraction: "#06B6D4",
  summarization: "#84CC16",
  workflow_n8n: "#6366F1",
  concept_extraction: "#14B8A6",
  logo_detection: "#F97316",
  comments_extraction: "#A855F7",
  media_geolocation: "#22C55E",
} as const;

// ============================================================================
// Alert Type Colors
// ============================================================================

/**
 * Couleurs pour les types d'alertes
 * Appliquées sur: icônes d'alertes, badges de type, colonnes de board
 */
export const alertTypeColors = {
  new_documents: "#3B82F6",
  threshold: "#10B981",
  change: "#F59E0B",
  anomaly: "#8B5CF6",
  custom: "#EC4899",
} as const;

// ============================================================================
// Vibrant Colors Palette (for dynamic groupings)
// ============================================================================

/**
 * Palette de couleurs vibrantes pour les groupements dynamiques
 * Appliquées sur: colonnes de board groupées, catégories, séries de données
 */
export const vibrantColors = [
  "#F59E0B", // Ambre
  "#10B981", // Émeraude
  "#3B82F6", // Bleu
  "#8B5CF6", // Violet
  "#EC4899", // Rose
  "#EF4444", // Rouge
  "#06B6D4", // Cyan
  "#84CC16", // Lime
] as const;

// ============================================================================
// Type Exports
// ============================================================================

export type StatusColorKey = keyof typeof statusColors;
export type AlertTypeColorKey = keyof typeof alertTypeColors;
export type SemanticColorKey = keyof typeof semanticColors;
export type SemanticColorVariant = "light" | "main" | "dark";
export type GreyScaleKey = keyof typeof greyScale;
export type ChartColorKey = keyof typeof chartColors;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get a semantic color value
 */
export function getSemanticColor(
  color: SemanticColorKey,
  variant: SemanticColorVariant = "main"
): string {
  return semanticColors[color][variant];
}

/**
 * Get a grey scale value
 */
export function getGrey(shade: GreyScaleKey): string {
  return greyScale[shade];
}

/**
 * Get a chart color by index (wraps around if index > palette length)
 */
export function getChartColor(index: number): string {
  return chartColorsArray[index % chartColorsArray.length];
}

/**
 * Get a vibrant color by index (wraps around if index > palette length)
 */
export function getVibrantColor(index: number): string {
  return vibrantColors[index % vibrantColors.length];
}

/**
 * Get a status color
 */
export function getStatusColor(status: StatusColorKey): string {
  return statusColors[status];
}

/**
 * Get an enrichment type color
 */
export function getEnrichmentTypeColor(type: string): string {
  return enrichmentTypeColors[type] || statusColors.inactive;
}

/**
 * Get an alert type color
 */
export function getAlertTypeColor(type: AlertTypeColorKey): string {
  return alertTypeColors[type] || statusColors.inactive;
}

// ============================================================================
// SCSS Variable Generation (for build-time SCSS compatibility)
// ============================================================================

/**
 * Generates SCSS variable declarations from the color definitions
 * This can be used by build tools to generate _colors.scss
 */
export function generateScssVariables(): string {
  const lines: string[] = [
    "// Auto-generated from colors.ts - DO NOT EDIT MANUALLY",
    "",
    "// Semantic Colors",
  ];

  // Semantic colors
  for (const [colorName, variants] of Object.entries(semanticColors)) {
    for (const [variant, value] of Object.entries(variants)) {
      const varName =
        variant === "main"
          ? `$${colorName}Main`
          : `$${colorName}${variant.charAt(0).toUpperCase() + variant.slice(1)}`;
      lines.push(`${varName}: ${value};`);
    }
  }

  lines.push("", "// Grey Scale");
  for (const [shade, value] of Object.entries(greyScale)) {
    lines.push(`$grey${shade}: ${value};`);
  }

  lines.push("", "// Chart Colors");
  for (const [name, value] of Object.entries(chartColors)) {
    lines.push(`$chart${name.charAt(0).toUpperCase() + name.slice(1)}: ${value};`);
  }

  return lines.join("\n");
}
